/**
 * Campaign setup — the form a GM fills in before their first session.
 *
 * Ordered the way a GM actually thinks about a table: the world, then where
 * tonight starts, then tone, then who is playing, then what they want from
 * the tool. The per-player fields matter more than they look — the agent uses
 * them to decide who to spotlight and when, so a table with them filled in
 * gets noticeably better pacing than one without.
 *
 * Everything autosaves; see useDraft.
 */
import { useState, type ReactNode } from 'react';

import type { CampaignDraft, PlayerDraft } from '@/campaign/premise';
import { compilePremise, emptyPlayer, slugify } from '@/campaign/premise';
import type { SaveState } from '@/campaign/useDraft';
import type { CampaignDraftRow } from '@/api/client';
import { DraftPicker } from '@/components/DraftPicker';
import { Select } from '@/components/Select';

interface Props {
  draft: CampaignDraft;
  saveState: SaveState;
  /** The stored draft being edited, so the list can mark it. */
  draftId?: string | null;
  /** Load a saved draft into the form. Omitted hides the picker. */
  onOpenDraft?: (row: CampaignDraftRow) => void;
  busy: boolean;
  error: string | null;
  onChange: <K extends keyof CampaignDraft>(
    key: K,
    value: CampaignDraft[K],
  ) => void;
  onSubmit: (
    premise: string,
    slug: string,
    name: string,
    draft: CampaignDraft,
  ) => void;
  onCancel?: () => void;
}

/**
 * The languages transcription actually supports, by the subtag it takes.
 *
 * A subset of the provider's sixty, chosen for tables likely to exist rather
 * than for completeness — a dropdown of sixty is a worse question than a
 * dropdown of twelve. The column accepts any valid subtag, so adding one here
 * is a one-line change and nothing else has to move.
 */
const LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Portuguese / Português' },
  { code: 'es', name: 'Spanish / Español' },
  { code: 'fr', name: 'French / Français' },
  { code: 'de', name: 'German / Deutsch' },
  { code: 'it', name: 'Italian / Italiano' },
  { code: 'nl', name: 'Dutch / Nederlands' },
  { code: 'pl', name: 'Polish / Polski' },
  { code: 'sv', name: 'Swedish / Svenska' },
  { code: 'ru', name: 'Russian / Русский' },
  { code: 'ja', name: 'Japanese / 日本語' },
  { code: 'zh', name: 'Chinese / 中文' },
  { code: 'ko', name: 'Korean / 한국어' },
];

export function CampaignSetup({
  draft,
  saveState,
  draftId,
  onOpenDraft,
  busy,
  error,
  onChange,
  onSubmit,
  onCancel,
}: Props) {
  const [showPremise, setShowPremise] = useState(false);
  const slug = draft.slug.trim() || slugify(draft.name);
  const canSubmit = Boolean(draft.name.trim() && draft.world.trim() && slug);

  function updatePlayer(index: number, patch: Partial<PlayerDraft>): void {
    onChange(
      'players',
      draft.players.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
  }

  return (
    <form
      className="setup"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(compilePremise(draft), slug, draft.name.trim(), draft);
      }}
    >
      <header className="setup__header">
        <div>
          <h2 className="setup__title">New campaign</h2>
          <p className="setup__lede">
            Start with a name and a world. Add your players and the details you
            want Throughline to remember as you go.
          </p>
        </div>
        {/* Do not render an empty badge before the first edit. It appeared as
            a stray outlined dash beside Saved drafts. */}
        {saveState !== 'idle' ? (
          <span className="setup__save" aria-live="polite">
            {/* "Draft saved" used to mean one key in this browser's localStorage,
                deleted the moment the campaign was created. It now means a row on
                the server too, so the words are worth what they say. `local-only`
                is the honest state when that write has not landed — private
                browsing, or offline. */}
            {saveState === 'saving'
              ? 'Saving…'
              : saveState === 'saved'
                ? 'Draft saved'
                : 'Saved on this device only'}
          </span>
        ) : null}
        {onOpenDraft ? (
          <DraftPicker currentId={draftId ?? null} onOpen={onOpenDraft} />
        ) : null}
      </header>

      <nav className="setup-jump" aria-label="Campaign setup sections">
        {['The campaign', 'Players and characters', 'About you'].map(
          (label, index) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                const section = document.getElementById(
                  `setup-section-${index}`,
                );
                section?.scrollIntoView({
                  block: 'start',
                  behavior: window.matchMedia(
                    '(prefers-reduced-motion: reduce)',
                  ).matches
                    ? 'instant'
                    : 'smooth',
                });
                section
                  ?.querySelector<HTMLElement>('input, button, textarea')
                  ?.focus({ preventScroll: true });
              }}
            >
              <span>0{index + 1}</span>
              {label}
            </button>
          ),
        )}
      </nav>
      <fieldset className="setup__group" id="setup-section-0">
        <legend className="setup__legend">The campaign</legend>

        <Field
          label="Campaign name"
          icon="campaign"
          value={draft.name}
          onChange={(v) => onChange('name', v)}
          placeholder="The Drowned Chapel"
          required
        />

        <Field
          label="World, setting, and premise"
          icon="world"
          value={draft.world}
          onChange={(v) => onChange('world', v)}
          placeholder="A mining town built over a flooded cathedral. The company owns the pumps, and the pumps have started failing."
          multiline
          rows={4}
          required
        />

        <Field
          label="Where tonight begins"
          icon="opening"
          value={draft.opening}
          onChange={(v) => onChange('opening', v)}
          placeholder="In the pump-house at shift change, water already ankle-deep."
          multiline
          rows={3}
          hint="Be literal about the location. If the party is travelling, say where they are right now, not where they are headed."
        />

        <Field
          label="Tone and pacing"
          icon="tone"
          value={draft.tone}
          onChange={(v) => onChange('tone', v)}
          placeholder="Slow dread with comic relief between scenes. Combat is rare and scary."
          multiline
          rows={2}
        />
        <SystemField
          systemKey={draft.systemKey}
          notes={draft.systemNotes}
          onSystemKey={(v) => onChange('systemKey', v)}
          onNotes={(v) => onChange('systemNotes', v)}
        />

        <Select
          label={
            <SetupQuestionLabel icon="language">
              What language does your table speak?
            </SetupQuestionLabel>
          }
          value={draft.language}
          onChange={(v) => onChange('language', v)}
          options={LANGUAGES.map((l) => ({ value: l.code, label: l.name }))}
          hint="The language Throughline listens to and uses for your narration, dialogue, and notes."
        />
      </fieldset>

      <fieldset className="setup__group" id="setup-section-1">
        <legend className="setup__legend">Players and characters</legend>
        <p className="setup__hint">
          Throughline uses this to share the spotlight around the table. It
          stays private to you.
        </p>

        {draft.players.map((player, index) => (
          <div className="player-card" key={index}>
            <div className="player-card__head">
              <strong>Player {index + 1}</strong>
              {draft.players.length > 1 ? (
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() =>
                    onChange(
                      'players',
                      draft.players.filter((_, i) => i !== index),
                    )
                  }
                >
                  Remove
                </button>
              ) : null}
            </div>
            <div className="player-card__row">
              <Field
                label="Player name"
                icon="player"
                value={player.playerName}
                onChange={(v) => updatePlayer(index, { playerName: v })}
                placeholder="Sam"
              />
              <Field
                label="Character name"
                icon="character"
                value={player.characterName}
                onChange={(v) => updatePlayer(index, { characterName: v })}
                placeholder="Berrin Cask"
              />
              <Field
                label="Level"
                icon="level"
                value={player.level}
                onChange={(v) =>
                  updatePlayer(index, { level: v.replace(/[^0-9]/g, '') })
                }
                placeholder="5"
                hint="Helps balance encounters for your party."
              />
            </div>
            <Field
              label="Character backstory"
              icon="backstory"
              value={player.backstory}
              onChange={(v) => updatePlayer(index, { backstory: v })}
              placeholder="Former company diver. Left after a collapse he refuses to talk about."
              multiline
              rows={2}
            />
            <Field
              label="Private player notes"
              icon="private"
              value={player.playerDetails}
              onChange={(v) => updatePlayer(index, { playerDetails: v })}
              placeholder="Loves a moral dilemma, checks out during long combat."
              multiline
              rows={2}
            />
          </div>
        ))}

        <button
          type="button"
          className="button"
          onClick={() => onChange('players', [...draft.players, emptyPlayer()])}
        >
          Add player
        </button>
      </fieldset>

      <fieldset className="setup__group" id="setup-section-2">
        <legend className="setup__legend">About you</legend>

        <Select
          label={
            <SetupQuestionLabel icon="experience">
              How much have you used Throughline?
            </SetupQuestionLabel>
          }
          value={draft.throughlineExperience}
          onChange={(v) => onChange('throughlineExperience', v)}
          options={[
            { value: '', label: 'Prefer not to say' },
            { value: 'First session', label: 'This is my first session' },
            { value: 'A few sessions', label: 'A few sessions' },
            { value: 'Regular user', label: 'I use it regularly' },
          ]}
        />

        <Field
          label="Your strengths, weaknesses, and what you want help with"
          icon="gm"
          value={draft.gmProfile}
          onChange={(v) => onChange('gmProfile', v)}
          placeholder="Good at voices and improv, bad at remembering threads I opened three sessions ago. Help me with continuity."
          multiline
          rows={3}
        />
      </fieldset>

      {error ? (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="setup__actions">
        <button
          type="submit"
          className="button button--primary"
          disabled={!canSubmit || busy}
        >
          {busy ? 'Creating…' : 'Create campaign'}
        </button>
        {onCancel ? (
          <button type="button" className="button" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setShowPremise((v) => !v)}
        >
          {showPremise ? 'Hide' : 'Preview'} what Throughline will read
        </button>
      </div>

      {showPremise ? (
        <pre className="setup__preview">{compilePremise(draft)}</pre>
      ) : null}
    </form>
  );
}

/**
 * Which system the table plays, and how this table plays it.
 *
 * Combat reads this to pick the idiom it writes stat blocks in and to decide
 * whether it can score difficulty at all — the encounter budget tables are 5e
 * only, and a fight in anything else is marked unverified rather than given a
 * number borrowed from the wrong rulebook. Before this field existed nothing
 * set it, so every campaign was treated as D&D 5e.
 *
 * The list is a shortcut, not a limit: "Something else" takes any name, and the
 * profile is compiled from the model's knowledge of whatever is typed.
 */
const SYSTEMS: { key: string; label: string }[] = [
  { key: 'dnd5e', label: 'D&D 5e' },
  { key: 'pf2e', label: 'Pathfinder 2e' },
  { key: 'coc7e', label: 'Call of Cthulhu 7e' },
  { key: 'fabula-ultima', label: 'Fabula Ultima' },
  { key: 'blades', label: 'Blades in the Dark' },
  { key: 'mothership', label: 'Mothership' },
  { key: 'pbta', label: 'Powered by the Apocalypse' },
];

function toSystemKey(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function SystemField({
  systemKey,
  notes,
  onSystemKey,
  onNotes,
}: {
  systemKey: string;
  notes: string;
  onSystemKey: (value: string) => void;
  onNotes: (value: string) => void;
}) {
  const known = SYSTEMS.some((s) => s.key === systemKey);
  const [other, setOther] = useState(known ? '' : systemKey);
  const custom = !known || Boolean(other);

  // Two sibling questions, not one nested inside the other. House rules used
  // to render *inside* the system field's own .field wrapper, so it inherited
  // that block's spacing and sat flush against the hint above it.
  return (
    <>
      <div className="field">
        <label
          className="field__label"
          id="system-key-label"
          htmlFor="system-key"
        >
          <SetupQuestionLabel icon="system">Game system</SetupQuestionLabel>
        </label>
        <Select
          id="system-key"
          labelledBy="system-key-label"
          value={custom ? 'other' : systemKey}
          onChange={(v) => {
            if (v === 'other') {
              setOther(' ');
              onSystemKey('');
            } else {
              setOther('');
              onSystemKey(v);
            }
          }}
          options={[
            ...SYSTEMS.map((sys) => ({ value: sys.key, label: sys.label })),
            { value: 'other', label: 'Something else…' },
          ]}
        />
        {custom ? (
          <input
            className="field__input"
            style={{ marginTop: 'var(--space-2)' }}
            value={other.trim()}
            placeholder="Name the system"
            onChange={(e) => {
              setOther(e.target.value || ' ');
              onSystemKey(toSystemKey(e.target.value));
            }}
          />
        ) : null}
        <span className="field__hint">
          Stat blocks are written for this system. Difficulty is only scored
          for D&amp;D 5e, so fights in other systems are marked unverified
          instead of borrowing numbers from the wrong rulebook.
        </span>
      </div>

      <Field
        label="House rules worth knowing"
        icon="rules"
        value={notes}
        onChange={onNotes}
        placeholder="We use flanking. Death saves are secret. No multiclassing."
        multiline
        rows={2}
        hint="Where these disagree with the rulebook, these win."
      />
    </>
  );
}

function Field({
  label,
  icon,
  value,
  onChange,
  placeholder,
  hint,
  multiline,
  rows = 3,
  required,
}: {
  label: string;
  icon: SetupIconName;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span className="field__label">
        <SetupQuestionLabel icon={icon}>
          {label}
          {required ? <span aria-hidden="true"> *</span> : null}
        </SetupQuestionLabel>
      </span>
      {multiline ? (
        <textarea
          className="field__input field__input--multiline"
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        />
      ) : (
        <input
          className="field__input"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        />
      )}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

type SetupIconName =
  | 'campaign'
  | 'system'
  | 'rules'
  | 'language'
  | 'world'
  | 'opening'
  | 'tone'
  | 'player'
  | 'character'
  | 'level'
  | 'backstory'
  | 'private'
  | 'experience'
  | 'gm';

function SetupQuestionLabel({
  icon,
  children,
}: {
  icon: SetupIconName;
  children: ReactNode;
}) {
  return (
    <span className="setup-question__label">
      <span className="setup-question__icon" aria-hidden="true">
        <SetupQuestionIcon kind={icon} />
      </span>
      <span>{children}</span>
    </span>
  );
}

function SetupQuestionIcon({ kind }: { kind: SetupIconName }) {
  let drawing: ReactNode;
  switch (kind) {
    case 'campaign':
      drawing = (
        <>
          <path d="M4 17V3" />
          <path d="M4 4h10l-2 3 2 3H4" />
        </>
      );
      break;
    case 'system':
      drawing = (
        <>
          <path d="m10 2 7 5-2.7 9H5.7L3 7l7-5Z" />
          <path d="m3 7 7 3 7-3M10 10v7" />
        </>
      );
      break;
    case 'rules':
      drawing = (
        <>
          <path d="M4 5h12M4 10h12M4 15h12" />
          <circle cx="8" cy="5" r="1.5" />
          <circle cx="13" cy="10" r="1.5" />
          <circle cx="7" cy="15" r="1.5" />
        </>
      );
      break;
    case 'language':
      drawing = (
        <>
          <path d="M3 4h14v9H9l-4 3v-3H3V4Z" />
          <path d="M7 8h6M7 10.5h4" />
        </>
      );
      break;
    case 'world':
      drawing = (
        <>
          <circle cx="10" cy="10" r="7" />
          <path d="M3 10h14M10 3a11 11 0 0 1 0 14M10 3a11 11 0 0 0 0 14" />
        </>
      );
      break;
    case 'opening':
      drawing = (
        <>
          <path d="M15 8c0 4-5 9-5 9S5 12 5 8a5 5 0 0 1 10 0Z" />
          <circle cx="10" cy="8" r="1.7" />
        </>
      );
      break;
    case 'tone':
      drawing = (
        <>
          <path d="m10 2 1.2 4.1L15 8l-3.8 1.9L10 14l-1.2-4.1L5 8l3.8-1.9L10 2Z" />
          <path d="m15.5 13 .6 2 1.9.8-1.9.8-.6 2-.6-2-1.9-.8 1.9-.8.6-2Z" />
        </>
      );
      break;
    case 'player':
      drawing = (
        <>
          <circle cx="10" cy="6" r="3" />
          <path d="M4.5 17a5.5 5.5 0 0 1 11 0" />
        </>
      );
      break;
    case 'character':
      drawing = (
        <>
          <path d="M10 2.5 16 5v4.5c0 3.6-2.5 6-6 8-3.5-2-6-4.4-6-8V5l6-2.5Z" />
          <path d="m7.5 10 1.7 1.7 3.5-3.7" />
        </>
      );
      break;
    case 'level':
      drawing = (
        <>
          <path d="M3 16h4v-4h4V8h4V4h2" />
          <path d="m13.5 6.5 3.5-3.5 3.5 3.5" transform="translate(-2)" />
        </>
      );
      break;
    case 'backstory':
      drawing = (
        <>
          <path d="M3.5 4.5A8 8 0 0 1 10 6v11a8 8 0 0 0-6.5-1.5v-11ZM16.5 4.5A8 8 0 0 0 10 6v11a8 8 0 0 1 6.5-1.5v-11Z" />
        </>
      );
      break;
    case 'private':
      drawing = (
        <>
          <rect x="4" y="8" width="12" height="9" rx="2" />
          <path d="M7 8V6a3 3 0 0 1 6 0v2M10 12v2" />
        </>
      );
      break;
    case 'experience':
      drawing = (
        <>
          <circle cx="10" cy="10" r="7" />
          <path d="M10 6v4l3 2M4.5 4.5 3 3" />
        </>
      );
      break;
    case 'gm':
      drawing = (
        <>
          <circle cx="10" cy="10" r="7" />
          <circle cx="10" cy="10" r="3" />
          <path d="M10 1.5v3M10 15.5v3M1.5 10h3M15.5 10h3" />
        </>
      );
      break;
  }

  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {drawing}
    </svg>
  );
}
